import { seg } from "./mirrorRoomMath";
import ring from "../../assets/img/portal-ring.png";
import "./closeFriendMark.css";

/**
 * The close-friends toggle: a small disco ball. Off it's outlined on white; on
 * it's a black block with the four facets lit. Friends only — callers render
 * it just when the relationship is `friends`.
 *
 * size: 'corner' (a card's top-right cell, left + bottom border only) or a row
 * size 28 | 32 | 36 | 40 | 44. `q` is the room's progress where the mark stays
 * above the room (a card, a /friends row) so its facets light clockwise with it;
 * rows the room covers pass nothing.
 * `inverted` is a highlighted (black) search row.
 */
const SIZES = {
  corner: { box: 40, icon: 18 },
  28: { box: 28, icon: 18 },
  32: { box: 32, icon: 20 },
  36: { box: 36, icon: 22 },
  40: { box: 40, icon: 18 },
  44: { box: 44, icon: 24 },
};

const FACETS = [
  { d: "M12 7.2A6.8 6.8 0 0 1 18.8 14H12z", fill: "#FFCCE6" },
  { d: "M18.8 14A6.8 6.8 0 0 1 12 20.8V14z", fill: "#A8D8EA" },
  { d: "M12 20.8A6.8 6.8 0 0 1 5.2 14H12z", fill: "#C6B8FF" },
  { d: "M5.2 14A6.8 6.8 0 0 1 12 7.2V14z", fill: "#FFF8EA" },
];

export function DiscoMarkIcon({ size = 24, on = false, q = null }) {
  const facet = (i) =>
    on ? (q == null ? 1 : seg(q, 0.04 + 0.03 * i, 0.09 + 0.03 * i)) : 0;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="square"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
    >
      <g stroke="none">
        {FACETS.map((f, i) => (
          <path
            key={f.fill}
            d={f.d}
            fill={f.fill}
            style={{ opacity: facet(i) }}
          />
        ))}
      </g>
      <path d="M12 2.4v4.8" />
      <circle cx="12" cy="14" r="6.8" />
      <path d="M5.2 14h13.6M12 7.2v13.6" />
    </svg>
  );
}

export default function CloseFriendMark({
  on,
  onToggle,
  size = 32,
  name = "",
  q = null,
  inverted = false,
  className = "",
  markRef,
}) {
  const { box, icon } = SIZES[size] || SIZES[32];
  const corner = size === "corner";

  const who = name || "this friend";
  return (
    <button
      ref={markRef}
      type="button"
      aria-pressed={on}
      aria-label={
        on ? `Remove ${who} from close friends` : `Add ${who} to close friends`
      }
      title={on ? "Close friend — click to remove" : "Add to close friends"}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onToggle();
      }}
      className={`close-friend-mark ${on ? "is-on" : ""} ${
        inverted ? "is-inverted" : ""
      } ${corner ? "is-corner" : ""} ${className}`}
      style={{ width: box, height: box }}
    >
      {on && <img className="close-friend-ring" src={ring} alt="" />}
      <span className="close-friend-face">
        <DiscoMarkIcon size={icon} on={on} q={q} />
      </span>
    </button>
  );
}

/** Keeps a column of row actions aligned where a person isn't a friend. */
export function CloseFriendSpacer({ size = 28 }) {
  const { box } = SIZES[size] || SIZES[28];
  return (
    <span
      aria-hidden="true"
      className="block flex-shrink-0"
      style={{ width: box, height: box }}
    />
  );
}
